import { RawParams, Transition } from "@uirouter/core";
import { Component, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import { Observable, ReplaySubject, forkJoin, from, map, take, takeUntil, zip } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AuthenticationResolverService, getAuth0UserId } from "@gtmhub/auth";
import { IColoringSetup, ProgressColoring } from "@gtmhub/configuration";
import { Ng1StateProxy } from "@gtmhub/core/routing";
import { BetaFeaturesService, FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { HomeService } from "@gtmhub/home/services/home";
import { InsightboardService } from "@gtmhub/insightboards";
import { NotificationsInboxService } from "@gtmhub/notifications/controllers/notifications-inbox";
import { IReport } from "@gtmhub/reporting/models";
import { setTopNavBarProps } from "@gtmhub/shared/components/ui/top-navigation-bar/utils";
import { INotificationsCountData } from "@gtmhub/sockets";
import { CurrentUserService } from "@gtmhub/users";
import { UserProfileService } from "@gtmhub/users/userProfile/profileService";
import { AccountPickerComponent } from "@webapp/accounts/components/account-picker/account-picker.component";
import { MultiAccount, UserMultiAccountInvitationType } from "@webapp/accounts/models/multi-account/multi-account.models";
import { MultiAccountsFacade } from "@webapp/accounts/services/multi-account/multi-account-facade.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { IScreen, Screens } from "@webapp/navigation/models/shared.models";
import { SettingsSubNavStateService } from "@webapp/navigation/services/settings/settings-sub-nav-state.service";
import { SettingsVisibilityService } from "@webapp/navigation/services/settings/settings-visibility.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { DropdownMenuItem } from "@webapp/shared/gh-dropdown/gh-dropdown.models";
import { GhDropdownMenuItemBuilder } from "@webapp/shared/gh-dropdown/util/gh-dropdown-menu-item-builder";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { IProfile } from "@webapp/users/models/users.models";
import { NotificationsFacade } from "../../../notification/services/notifications/notifications-facade.service";
import { ActiveNavScreenService } from "../../services/active-nav-screen.service";
import { setNavWidth } from "../../utils/navigation.util";
import { NAV_WIDTH, TOP_NAV_BAR_PROPS } from "./config";

@Component({
  selector: "navigation-v2",
  templateUrl: "./navigation.component.html",
  styleUrls: ["./navigation.component.less"],
})
export class NavigationV2Component implements OnInit, OnDestroy {
  public screens: IScreen[] = [];
  public settingsScreen: IScreen = {
    icon: "manage",
    state: "gtmhub.configuration.methodology",
    key: "settings",
    showScreenCondition$: this.settingsVisibility.showSettings$,
    subStates: [
      "gtmhub.configuration.methodology",
      "gtmhub.configuration.language",
      "gtmhub.configuration.reportsAndNotifications",
      "gtmhub.configuration.tags",
      "gtmhub.configuration.features",
      "gtmhub.configuration.customFields",
      "gtmhub.configuration.parameters",
      "gtmhub.automationList",
      "gtmhub.usersList",
      "gtmhub.badges",
      "gtmhub.roles",
      "gtmhub.dataflow",
      "gtmhub.configuration.pluginList",
      "gtmhub.configuration.apiTokens",
      "gtmhub.configuration.slack",
      "gtmhub.configuration.jira",
      "gtmhub.configuration.workday",
      "gtmhub.configuration.accountNameDomainChange",
      "gtmhub.configuration.branding",
      "gtmhub.configuration.billing",
      "gtmhub.configuration.singleSignOn",
      "gtmhub.configuration.auditLog",
    ],
  };
  public profile: IProfile;
  public activeScreen: Screens;
  public profileMenuItems: DropdownMenuItem[];
  public helpMenuItems: DropdownMenuItem[];
  public paramsMap: Map<Screens, RawParams> = new Map();
  public unreadNotificationsCount$: Observable<number>;
  public showMarketplace$: Observable<boolean>;
  private reportIds: string[];
  private userAccounts: MultiAccount[];
  private isMultiAccountEnabled: boolean;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private ng1StateProxy: Ng1StateProxy,
    private activeNavStateService: ActiveNavScreenService,
    private homeService: HomeService,
    private insightboardService: InsightboardService,
    private userProfileService: UserProfileService,
    private authenticationResolverService: AuthenticationResolverService,
    private betaFeaturesService: BetaFeaturesService,
    private accountService: AccountService,
    private progressColoring: ProgressColoring,
    private currentUserService: CurrentUserService,
    private multiAccountsFacade: MultiAccountsFacade,
    private featureToggleService: FeatureToggleService,
    private modalService: UiModalService,
    private viewContainerRef: ViewContainerRef,
    private notificationsFacade: NotificationsFacade,
    private notificationsInboxService: NotificationsInboxService,
    private permissionsFacade: PermissionsFacade,
    private broadcastService: BroadcastService,
    private settingsVisibility: SettingsVisibilityService,
    private settingsSubNavStateService: SettingsSubNavStateService
  ) {
    setNavWidth({ expanded: NAV_WIDTH.expanded + "px", collapsed: NAV_WIDTH.collapsed + "px" });
    setTopNavBarProps({ height: TOP_NAV_BAR_PROPS.height + "px", borderColor: TOP_NAV_BAR_PROPS.borderColor });
    this.setColoringScale();
    this.ng1StateProxy.stateChange$.subscribe((transition: Transition) => {
      if (transition.success) {
        this.setActiveScreen();
      }
    });
  }

  public ngOnInit(): void {
    this.showMarketplace$ = this.permissionsFacade.hasPermission$("ManageGoals");

    this.settingsSubNavStateService
      .firstVisibleState$()
      .pipe(take(1))
      .subscribe((v) => (this.settingsScreen.state = v));

    this.activeNavStateService
      .getNavStateChange$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screen) => {
        this.activeScreen = screen;
      });

    this.screens = this.getScreens();

    this.broadcastService.on("FeatureFlagUpdated").subscribe(() => {
      this.screens = this.getScreens();
    });

    this.activeNavStateService.getNavStateChange$().subscribe((screen) => {
      this.activeScreen = screen;
    });

    this.insightboardService.getReports().then((reports: IReport[]) => {
      this.homeService.cachedReports = reports;
      const performanceReport = this.homeService.cachedReports.find((report) => report.name.toLowerCase() === "performance report");
      this.paramsMap["reports"] = { dashboardId: performanceReport.id };
      this.reportIds = reports.map((r) => r.id);
      this.setActiveScreen();
    });

    forkJoin({
      accounts: this.multiAccountsFacade.getMultiAccounts$(getAuth0UserId()),
      isLockMultiAccountEnabled: this.isMultiAccountsFeatureEnabled(),
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ accounts, isLockMultiAccountEnabled }) => {
        this.userAccounts = accounts;
        this.isMultiAccountEnabled = !isLockMultiAccountEnabled;
        this.profileMenuItems = this.getProfileMenuItems();
      });

    this.multiAccountsFacade.getMultiAccounts$(getAuth0UserId()).subscribe((accounts) => (this.userAccounts = accounts));
    this.isMultiAccountsFeatureEnabled().subscribe((isLockMultiAccountEnabled) => (this.isMultiAccountEnabled = !isLockMultiAccountEnabled));

    this.getCurrentUserDetails();
    this.helpMenuItems = this.getHelpMenuItems();

    this.unreadNotificationsCount$ = this.notificationsFacade.getUnreadNotificationsCount$();
    this.subscribeToNotificationsCountUpdates();

    const previousSeenNewNaviCount = Number(localStorage.getItem("seenNewNaviCount"));
    const seenNewNaviCount = previousSeenNewNaviCount ? previousSeenNewNaviCount + 1 : 1;
    localStorage.setItem("seenNewNaviCount", JSON.stringify(seenNewNaviCount));
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public openNotificationsModal(count: number): void {
    this.notificationsInboxService.open({}, count);
  }

  public setActive(screen: IScreen): void {
    if (!screen.key) return;
    this.activeScreen = screen.key;
  }

  private logout(): void {
    this.authenticationResolverService.logout({ ssoLogoutIfEnabled: true });
  }

  private getCurrentUserDetails(): void {
    this.profile = this.userProfileService.getProfile();
  }

  private async toggleNewExperience(): Promise<void> {
    // old and new nav do not share the same toggle button
    // therefore from the new navigation we always switch to false
    await this.betaFeaturesService.optForBetaFeature("new-experience-toggle", false);
    document.location.reload();
  }

  private setColoringScale(): void {
    const accountColoring = this.accountService.getAccountSetting<IColoringSetup>("coloring");
    this.progressColoring.setColoring(accountColoring);
  }

  private getProfileMenuItems(): DropdownMenuItem[] {
    // Localize
    const menuItemBuilder = new GhDropdownMenuItemBuilder();
    const menuOpts = [
      menuItemBuilder
        .setKey("New Gtmhub")
        .setAction({
          handler: () => this.toggleNewExperience(),
        })
        .setToBeEndOfSection()
        .setUiType({ uiType: "toggle", value: this.getNewGtmhubToggled() })
        .setTrack("New Experience Switch")
        .setTrackMetaData({ direction: "new_to_old" })
        .build(),

      menuItemBuilder.setKey("Edit profile").setAction({ state: "gtmhub.userProfile" }).build(),

      menuItemBuilder.setKey("Manage notifications").setAction({ state: "gtmhub.userNotificationSettings" }).setToBeEndOfSection().build(),
      // Add logout icon
      menuItemBuilder
        .setKey("Logout")
        .setAction({ handler: () => this.logout() })
        .setUiType({ uiType: "icon", icon: "" })
        .build(),
    ];

    if (this.isAllowedToSwitchAccount()) {
      const switchAccountsOpt = menuItemBuilder
        .setKey("Switch account")
        .setAction({ handler: () => this.openAccountsModal() })
        .build();

      menuOpts.splice(menuOpts.length - 1, 0, switchAccountsOpt);
    }

    return menuOpts;
  }

  private getHelpMenuItems(): DropdownMenuItem[] {
    // Localize
    const menuItemBuilder = new GhDropdownMenuItemBuilder();

    const liveFaqItem = menuItemBuilder
      .setKey("live_faq_s")
      .setUiType({ uiType: "icon", icon: "help" })
      .setAction({ href: "https://help.gtmhub.com/en/", target: "_blank" })
      .build();

    const resourceLibararyItem = menuItemBuilder
      .setKey("resource_library")
      .setUiType({ uiType: "icon", icon: "edit" })
      .setAction({ href: "https://gtmhub.com/resources", target: "_blank" })
      .build();

    const productUpdatesItem = menuItemBuilder
      .setKey("product_updates")
      .setUiType({ uiType: "icon", icon: "manage" })
      .setAction({ href: "https://updates.gtmhub.com/", target: "_blank" })
      .build();

    const platformStatusItem = menuItemBuilder
      .setKey("platform_status")
      .setUiType({ uiType: "icon", icon: "refresh" })
      .setAction({ href: "https://status.gtmhub.com/", target: "_blank" })
      .build();

    const giveFeedbackItem = menuItemBuilder
      .setKey("give_feedback")
      .setUiType({ uiType: "icon", icon: "comments" })
      .setAction({ href: "https://gtmhub.atlassian.net/servicedesk/customer/portal/1", target: "_blank" })
      .build();

    return [liveFaqItem, resourceLibararyItem, productUpdatesItem, platformStatusItem, giveFeedbackItem];
  }

  public setMarketplaceCookie(): void {
    this.currentUserService.setMarketplaceRedirectCookie();
  }

  public getState(screen: IScreen): string {
    if (!this.paramsMap[screen.key]) {
      return screen.state;
    }

    const rawParamsToStr = JSON.stringify(this.paramsMap[screen.key]);
    return `${screen.state}(${rawParamsToStr})`;
  }

  private getNewGtmhubToggled(): boolean {
    // old and new nav do not share the same toggle button therefore
    // from the new navigation the value is always true
    return true;
  }

  private setActiveScreen(): void {
    const currentState = this.ng1StateProxy.getCurrentState();

    if (currentState === "gtmhub.insightboard") {
      const idParam = this.ng1StateProxy.getParam("dashboardId");
      this.activeScreen = this.reportIds.includes(idParam) ? "reports" : "insightboards";
      return;
    }

    const allScreens = [...this.screens, this.settingsScreen];
    const currentScreen = allScreens.find((s) => currentState === s.state || s.subStates?.some((subState) => currentState === subState));

    this.activeScreen = currentScreen?.key || "home";
  }

  private isAllowedToSwitchAccount(): boolean {
    return (
      this.isMultiAccountEnabled &&
      this.userAccounts &&
      !!this.userAccounts.find((acc) => acc.invitation && acc.invitation.status === UserMultiAccountInvitationType.approved)
    );
  }

  private openAccountsModal(): void {
    this.modalService.create({
      uiTitle: "Switch account",
      uiContent: AccountPickerComponent,
      uiClassName: "account-picker",
      uiCloseIcon: "close",
      uiFooter: null,
      uiWidth: 600,
      uiViewContainerRef: this.viewContainerRef,
      uiComponentParams: {
        accounts: this.userAccounts,
      },
    });
  }

  private isMultiAccountsFeatureEnabled(): Observable<boolean> {
    return from(this.featureToggleService.isFeatureAvailable(FeatureFlag.LockMultiAccount));
  }

  private subscribeToNotificationsCountUpdates(): void {
    const updateCount = (count: number): void => this.notificationsFacade.updateUnreadNotificationsCount(count);
    this.broadcastService.on<number>("newMessagesCount").pipe(takeUntil(this.destroyed$)).subscribe(updateCount);
    this.broadcastService
      .on<INotificationsCountData>("notificationsCount")
      .pipe(
        map((collection) => collection.totalCount),
        takeUntil(this.destroyed$)
      )
      .subscribe(updateCount);
  }

  private getScreens(): IScreen[] {
    return [
      {
        icon: "home",
        state: "gtmhub.home",
        key: "home",
      },
      {
        icon: "target",
        state: "gtmhub.sessions",
        key: "sessions",
        subStates: ["gtmhub.goals.all", "gtmhub.goals.all.session.form", "gtmhub.sessions._form.form"],
        showScreenCondition$: this.permissionsFacade.hasPermission$("AccessGoals").pipe(map((v) => v && this.accountService.featureAvailable("okrs"))),
      },
      {
        icon: "chart",
        state: "gtmhub.kpis",
        key: "kpis",
        showScreenCondition$: zip(
          from(this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleKpis)),
          this.permissionsFacade.hasPermission$("AccessInsightboards")
        ).pipe(
          map(([isFeatureAvailable, hasPermission]) => {
            return isFeatureAvailable && hasPermission && this.accountService.featureAvailable("kpis");
          })
        ),
      },
      {
        icon: "task",
        state: "gtmhub.tasks",
        key: "tasks",
        subStates: ["gtmhub.tasks._task.task"],
        showScreenCondition$: from(this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleTasks)).pipe(
          map((v) => v && this.accountService.featureAvailable("hub.tasks"))
        ),
      },
      {
        icon: "whiteboards",
        state: "gtmhub.whiteboards",
        key: "whiteboards",
        subStates: ["gtmhub.whiteboard"],
        showScreenCondition$: from(this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleWhiteboards)).pipe(
          map((v) => v && this.accountService.featureAvailable("whiteboards"))
        ),
      },
      {
        icon: "pie",
        state: "gtmhub.insightboard",
        key: "reports",
        subStates: ["gtmhub.lists", "gtmhub.list"],
      },
      {
        icon: "consulting",
        state: "gtmhub.insightboards",
        key: "insightboards",
        subStates: ["gtmhub.insightboard", "gtmhub.insightboard._edit.edit"],
        showScreenCondition$: this.permissionsFacade.hasPermission$("AccessInsightboards").pipe(map((v) => v && this.accountService.featureAvailable("insightboards"))),
      },
      {
        icon: "users-group",
        state: "gtmhub.employees",
        key: "people",
        subStates: ["gtmhub.employees.employee", "gtmhub.teams", "gtmhub.teams._form.form", "gtmhub.organization", "gtmhub.teams.team", "gtmhub.organization_chart"],
        showScreenCondition$: this.permissionsFacade.hasPermission$("AccessPeople"),
      },
    ];
  }
}
